import {AnswerStatus, QuestionDifficulty} from '@domain/pom'
import React, {useCallback, useMemo} from 'react'
import {NavigateFunction, useNavigate} from 'react-router'

import {CompletionCard} from '../components'
import {
  useSectionActions,
  usePomNavigation,
  usePlantUpmId,
  useStringSearchParameter,
  useQuestionState
} from '../hooks'
import {BackendQuestion, Section, Topic} from '../types'
import {countAllTopicsSubmissions} from '../utils'

import {PlantSideCardContainer} from './PlantSideCardContainer'
import {QuestionListSideCardContainer} from './QuestionListSideCardContainer'
import {SectionTopContainer} from './SectionTopContainer'

interface SectionContainerProps {
  section: Section
}

export const SectionContainer: React.FC<SectionContainerProps> = ({section}) => {
  const topics: Topic[] = section.topics
  const plantId = usePlantUpmId()
  const nav = usePomNavigation()
  const navigate: NavigateFunction = useNavigate()
  const {data: actions, status} = useSectionActions(section.id)
  const [, setQuestionId] = useStringSearchParameter('selectedQuestionId')

  const [{activeAnswerStatus, activeDifficulty}, {setActiveDifficulty, setActiveAnswerStatus}] =
    useQuestionState()

  const onScoreSectionClick = useCallback(
    (status?: AnswerStatus) => {
      setActiveAnswerStatus(status)
      setQuestionId(undefined)
    },
    [setActiveAnswerStatus, setQuestionId]
  )
  const onDifficultySectionClick = useCallback(
    (difficulty: QuestionDifficulty) => {
      setActiveDifficulty(difficulty)
      setQuestionId(undefined)
    },
    [setActiveDifficulty, setQuestionId]
  )
  const submissionSummary = useMemo(() => {
    return countAllTopicsSubmissions(topics, activeDifficulty)
  }, [activeDifficulty, topics])

  const filterQuestions = useCallback(
    ({id}: BackendQuestion) => id.includes(section.id),
    [section.id]
  )

  return (
    <>
      <SectionTopContainer
        actionLoadingStatus={status}
        isLoading={!actions || !topics}
        sideCardComponent={
          activeAnswerStatus ? (
            <QuestionListSideCardContainer
              difficulty={submissionSummary.difficulty}
              customQuestionsFilterFunction={filterQuestions}
            />
          ) : (
            <PlantSideCardContainer
              onSectionRowClick={(id: string) => {
                void navigate(nav.buildSubSectionPath(plantId, section?.id, id))
              }}
              sections={topics}
              actions={(actions || []).filter((a) => a.status !== 'done')}
            />
          )
        }
      >
        <CompletionCard
          activeDifficulty={activeDifficulty}
          onScoreSectionClick={onScoreSectionClick}
          onDifficultySectionClick={onDifficultySectionClick}
          responsiblePerson={section.responsible}
          submissionSummary={submissionSummary}
          difficultySummaries={section.difficultySummaries}
        />
      </SectionTopContainer>
    </>
  )
}
